import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/dealership/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, Link2, Unlink, Sparkles, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function GmailLeadsBanner() {
  const qc = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ["dealer-gmail-status"],
    queryFn: async () => (await api.get("/integrations/gmail/status")).data,
  });

  const connectMut = useMutation({
    mutationFn: async () => (await api.get("/integrations/gmail/connect")).data,
    onSuccess: (d) => {
      if (d?.auth_url) window.open(d.auth_url, "_blank", "noopener");
      toast.message("Authorize CarGurus Gmail in the new tab, then click Sync.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const syncMut = useMutation({
    mutationFn: async () => (await api.post("/integrations/gmail/sync")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dealer-gmail-status"] });
      const dup = d?.duplicates ? ` · ${d.duplicates} duplicate(s) skipped` : "";
      if (!d?.imported) {
        toast.message(`Sync done — 0 new leads. Scanned ${d?.scanned ?? 0} CarGurus email(s)${dup}.`, {
          description: (d?.scanned ?? 0) === 0
            ? "No CarGurus emails found in the connected inbox. Make sure you connected the inbox that actually receives CarGurus leads."
            : "All scanned emails were already imported or matched existing leads.",
        });
      } else {
        toast.success(`Synced — ${d.imported} new CarGurus lead(s) imported${dup}.`);
      }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const disconnectMut = useMutation({
    mutationFn: async () => (await api.post("/integrations/gmail/disconnect")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dealer-gmail-status"] });
      toast.success("CarGurus Gmail disconnected.");
    },
  });

  const autoReplyMut = useMutation({
    mutationFn: async (enabled) => (await api.post("/integrations/gmail/auto-reply", { enabled })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["dealer-gmail-status"] });
      toast.success(d.enabled ? "AI auto-reply ON — leads get a reply ~5 min after their email." : "AI auto-reply turned off.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="mb-4 rounded-lg border border-amber-500/35 bg-amber-500/[0.07] px-4 py-3 text-sm text-amber-300 flex items-center gap-2" data-testid="dealer-gmail-banner-unconfigured">
        <Mail className="h-4 w-4 shrink-0" />
        CarGurus auto-import is not configured yet — Google OAuth credentials are missing on the server.
      </div>
    );
  }

  // LOUD RED alert when connected but the send scope is missing — this is the
  // #1 reason auto-reply "doesn't work" for admins. Impossible-to-miss version.
  if (status.connected && status.can_send === false) {
    return (
      <div className="mb-4 rounded-lg border-2 border-red-500 bg-red-500/[0.07] px-4 py-4" data-testid="dealer-gmail-banner-needs-scope">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-red-300 text-sm mb-1">AI auto-reply is OFF — Gmail is connected without send permission.</div>
            <div className="text-red-400 text-xs mb-2">
              <b>{status.email}</b> was authorized in read-only mode. New CarGurus leads are being imported but NO replies are going out. Fix in 30 seconds:
            </div>
            <ol className="text-red-400 text-xs list-decimal ml-4 space-y-0.5 mb-3">
              <li>Click <b>Reconnect Gmail</b> below</li>
              <li>In the Google popup, check the box for <b>&quot;Send email on your behalf&quot;</b></li>
              <li>Click <b>Continue</b> — you&apos;re done</li>
            </ol>
            <div className="flex gap-2">
              <Button size="sm" variant="default" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => connectMut.mutate()} disabled={connectMut.isPending} data-testid="dealer-gmail-reconnect-banner">
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> Reconnect Gmail
              </Button>
              <Button size="sm" variant="outline" onClick={() => disconnectMut.mutate()} disabled={disconnectMut.isPending}>
                <Unlink className="h-3.5 w-3.5 mr-1.5" /> Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-border bg-secondary/40 px-4 py-3 space-y-2" data-testid="dealer-gmail-banner">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 text-sm">
        <Mail className="h-4 w-4 text-primary shrink-0" />
        {status.connected ? (
          <span>
            CarGurus leads auto-import from <strong>{status.email || "your inbox"}</strong>
            {status.last_sync && <span className="text-muted-foreground"> · last sync {new Date(status.last_sync).toLocaleString()}</span>}
            {typeof status.imported_total === "number" && <span className="text-muted-foreground"> · {status.imported_total} imported</span>}
          </span>
        ) : (
          <span>Connect the dealership Gmail to auto-import <strong>CarGurus</strong> leads.</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {status.connected ? (
          <>
            <Button size="sm" variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending} data-testid="dealer-gmail-sync-button">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncMut.isPending ? "animate-spin" : ""}`} /> Sync now
            </Button>
            <Button size="sm" variant="ghost" onClick={() => disconnectMut.mutate()} disabled={disconnectMut.isPending} data-testid="dealer-gmail-disconnect-button">
              <Unlink className="h-3.5 w-3.5 mr-1.5" /> Disconnect
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => connectMut.mutate()} disabled={connectMut.isPending} data-testid="dealer-gmail-connect-button">
            <Link2 className="h-3.5 w-3.5 mr-1.5" /> Connect Gmail
          </Button>
        )}
      </div>
      </div>
      {status.connected && status.can_send && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-2" data-testid="dealer-auto-reply-row">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <span>
              <strong>AI auto-reply</strong> — emails each new lead ~5 min after their inquiry: still available + CarGurus link + test-drive invite
              {status.auto_replied_total > 0 && <span className="text-muted-foreground"> · {status.auto_replied_total} sent</span>}
            </span>
          </div>
          <Switch
            checked={status.auto_reply_enabled !== false}
            onCheckedChange={(v) => autoReplyMut.mutate(v)}
            disabled={autoReplyMut.isPending}
            data-testid="dealer-auto-reply-toggle"
          />
        </div>
      )}
    </div>
  );
}
